import React from 'react';
import { useWatch } from 'react-hook-form';
import { arrayUtils, Body, Headline, TextBlock } from '@sato/components';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api/index';
import { HookFormDropdown } from '@components/ReactHookFormFields/HookFormDropdown';
import { HookFormFileInput } from '@components/ReactHookFormFields/HookFormFileInput';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { HookFormTextInput } from '@components/ReactHookFormFields/HookFormTextInput';
import { BODY_MAX_LENGTH, TOPIC_MAX_LENGTH } from '@constants';
import { useIncidentCreation } from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useLanguage } from '@hooks/useTranslate';
import { ApartmentAccessMethod, PetsInformation } from '@models/api';
import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';

import { IncidentFormWrapper } from '../../IncidentFormWrapper';
import { ApartmentAccessField } from '../components/ApartmentAccessField';
import { ApartmentDefectForm } from '../components/ApartmentDefectCommonForm';

import {
  ApplianceOption,
  parseApplianceOptions,
} from './ApartmentDefectAppliance.utils';

interface HomeApplianceDefectForm extends ApartmentDefectForm {
  description: string;
  appliance: string;
}

export const ApartmentDefectAppliance = ({
  customTranslations,
  parameters,
}: ParsedEntry<IWidget>) => {
  const { t, tContentful } = useCustomTranslations(customTranslations);
  const language = useLanguage();

  const applianceDropdownOptions = parseApplianceOptions(
    parameters?.appliancesSelectionItems,
    language
  );

  const { create } = useIncidentCreation({
    postIncident: api.incidents.postApartmentDefectIncident,
    classifications: {
      subClassification: IncidentSubClassification.HOME_APPLIANCE,
      classification: IncidentClassification.DEFECT_REPORT,
    },
  });

  return (
    <IncidentFormWrapper<HomeApplianceDefectForm>
      incidentKind="ApartmentDefectAppliance"
      defaultValues={{
        title: '',
        description: '',
        appliance: '',
        pets: undefined,
        access: ApartmentAccessMethod.OK,
        info: '',
        files: [],
      }}
      validationSchema={yup.object().shape({
        title: yup.string().required(),
        description: yup.string().required(),
        appliance: yup.string().required(),
        access: yup
          .string()
          .oneOf(Object.values(ApartmentAccessMethod))
          .required(),
        pets: yup.string().oneOf(Object.values(PetsInformation)).required(),
        info: yup.string(),
        files: yup.array().files(tContentful),
      })}
      onSubmit={({ appliance, description, ...values }) => {
        const applianceLabel =
          applianceDropdownOptions.find((item) => item.value === appliance)
            ?.label ?? '';

        return create({
          ...values,
          description: arrayUtils.joinArrayString(
            [
              `${t('messageTitlePrefix')}:\n - ${applianceLabel}`,
              `${t('descriptionHelper')}:\n - ${description}`,
            ],
            '\n\n'
          ),
        });
      }}
    >
      <div className="flex flex-col gap-3">
        <Headline level={3}>
          {tContentful('defectFormDescriptionTitle')}
        </Headline>

        <Body>{tContentful('defectFormDescriptionBody')}</Body>
      </div>

      <HookFormDropdown
        name="appliance"
        label={t('applianceSelectionLabel')}
        items={applianceDropdownOptions}
        floatingLabel
      />

      <HookFormTextInput
        type="text"
        name="title"
        label={{ value: t('titleLabel'), type: 'static' }}
        placeholder={t('titlePlaceholder')}
        maxLength={TOPIC_MAX_LENGTH}
      />

      <HookFormTextArea
        type="text"
        label={{ value: t('descriptionLabel'), type: 'static' }}
        name="description"
        placeholder={t('descriptionPlaceholder')}
        message={t('descriptionHelper')}
        rows={6}
        maxLength={BODY_MAX_LENGTH}
      />

      <ApplianceDescription options={applianceDropdownOptions} />

      <HookFormFileInput
        accept="application/pdf,image/jpeg,image/png"
        name="files"
        message={tContentful('supported_attachment_files')}
        label={tContentful('choose_files')}
      />

      <ApartmentAccessField />
    </IncidentFormWrapper>
  );
};

/** Shows the description of the currently selected appliance, if it has one */
const ApplianceDescription = ({ options }: { options: ApplianceOption[] }) => {
  const appliance = useWatch<HomeApplianceDefectForm, 'appliance'>({
    name: 'appliance',
  });

  const description = options.find(
    (option) => option.value === appliance
  )?.description;

  if (!description) {
    return null;
  }

  return (
    <TextBlock
      bodyVariant="normal"
      className="rounded-sm bg-brand-primary-ultralight p-3"
    >
      {description}
    </TextBlock>
  );
};
